import React, { memo, useState } from 'react';
import {
  Star,
  PackageCheck,
  FileText,
  Info,
  Clock
} from 'lucide-react';
import { Arquivo } from '../../types/gerArquivos';
import { ArquivoDetalhesModal } from './ArquivoDetalhesModal';
import { OrderItemTag, OrderTag } from '../ui';
import {
  buildClienteDisplay,
  buildMaterialSummary,
  formatArquivoData,
  getOrigemBadgeClass,
  getOrigemLabel,
  resolvePedidoItemRef,
  shouldShowArquivoStatusBadge,
} from './utils/arquivoDisplay';

const CATEGORIAS = {
  1: 'DTF TÊXTIL',
  2: 'DTF UV',
  3: 'SUBLIMÁTICA',
  4: 'CAMISETA'
} as const;

function getCategoriaColor(categoria: number): string {
  switch (categoria) {
    case 1: return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
    case 2: return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
    case 3: return 'bg-green-500/20 text-green-300 border-green-500/30';
    case 4: return 'bg-orange-500/20 text-orange-300 border-orange-500/30';
    default: return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
  }
}

function getStatusColor(status: string) {
  switch (status) {
    case 'salvo':
      return 'text-green-400 bg-green-400/10';
    case 'em_edicao':
      return 'text-yellow-400 bg-yellow-400/10';
    case 'finalizado':
      return 'text-blue-400 bg-blue-400/10';
    default:
      return 'text-gray-400 bg-gray-400/10';
  }
}

function getStatusText(status: string) {
  switch (status) {
    case 'salvo':
      return 'Salvo';
    case 'em_edicao':
      return 'Em edição';
    case 'finalizado':
      return 'Finalizado';
    default:
      return 'Desconhecido';
  }
}

function PedidoItemBadges({
  arquivo,
  size = 'xs',
  variant = 'compact',
}: {
  arquivo: Arquivo;
  size?: 'xs' | 'sm';
  variant?: 'default' | 'compact';
}) {
  const pedidoRef = resolvePedidoItemRef(arquivo);
  if (!pedidoRef) return null;

  if (pedidoRef.oitId != null) {
    return (
      <OrderItemTag
        orderId={pedidoRef.orcId}
        itemId={pedidoRef.oitId}
        size={size}
        variant={variant}
      />
    );
  }

  return <OrderTag orderId={pedidoRef.orcId} size={size} />;
}

function ArquivoMetaLinha({ arquivo }: { arquivo: Arquivo }) {
  const materialResumo = buildMaterialSummary(arquivo.materiais);
  const origemLabel = getOrigemLabel(arquivo);

  return (
    <div className="flex flex-wrap items-center gap-1.5 min-w-0">
      {arquivo.prodCatItem && (
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold border ${getCategoriaColor(arquivo.prodCatItem)}`}
        >
          {CATEGORIAS[arquivo.prodCatItem]}
        </span>
      )}
      {materialResumo && (
        <span
          className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium text-blue-300 bg-blue-500/10 max-w-[140px] truncate"
          title={materialResumo}
        >
          {materialResumo}
        </span>
      )}
      {origemLabel && (
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold border ${getOrigemBadgeClass(arquivo)}`}
        >
          {origemLabel}
        </span>
      )}
      <PedidoItemBadges arquivo={arquivo} />
      {shouldShowArquivoStatusBadge(arquivo.status) && (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${getStatusColor(arquivo.status)}`}>
          {getStatusText(arquivo.status)}
        </span>
      )}
    </div>
  );
}

function podeAbrirArquivo(arquivo: Arquivo): boolean {
  return arquivo.acoes?.podeAbrir ?? true;
}

function tooltipAbrirArquivo(arquivo: Arquivo): string {
  if (!podeAbrirArquivo(arquivo)) {
    return arquivo.acoes?.motivoBloqueio ?? 'Abertura não permitida para este arquivo';
  }
  return 'Abrir arquivo no CorelDRAW';
}

function podeFavoritarArquivo(arquivo: Arquivo): boolean {
  return arquivo.acoes?.podeFavoritar ?? arquivo.source !== 'cdr_save';
}

interface ListaArquivosProps {
  arquivos: Arquivo[];
  carregando: boolean;
  onToggleFavorito: (arquivo: Arquivo) => void;
  onAbrirArquivo: (arquivo: Arquivo) => void;
  onAbrirDiretorio: (arquivo: Arquivo) => void;
  temFiltrosAtivos?: boolean;
}

function ListaArquivosComponent({
  arquivos,
  carregando,
  onToggleFavorito,
  onAbrirArquivo,
  onAbrirDiretorio,
  temFiltrosAtivos = false
}: ListaArquivosProps) {
  const [arquivoDetalhes, setArquivoDetalhes] = useState<Arquivo | null>(null);

  if (carregando) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-gray-400">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-400"></div>
          <span>Carregando arquivos...</span>
        </div>
      </div>
    );
  }

  if (arquivos.length === 0) {
    return (
      <div className="p-8 text-center text-gray-400">
        <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>Nenhum arquivo encontrado</p>
        {temFiltrosAtivos && (
          <p className="text-sm mt-2">Tente ajustar os filtros de busca</p>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        <div className="hidden sm:block bg-navy-800 rounded-lg overflow-hidden">
          <div className="grid grid-cols-12 gap-3 p-3 bg-navy-700 text-xs font-medium text-gray-300 border-b border-navy-600">
            <div className="col-span-4">Arquivo</div>
            <div className="col-span-3">Cliente</div>
            <div className="col-span-2">Salvo em</div>
            <div className="col-span-3 text-right">Ações</div>
          </div>

          <div className="divide-y divide-navy-600">
            {arquivos.map((arquivo) => {
              const formattedDate = formatArquivoData(arquivo.dataCriacao);
              const clienteDisplay = buildClienteDisplay(arquivo.cliente);
              const abrirHabilitado = podeAbrirArquivo(arquivo);

              return (
              <div
                key={arquivo.id}
                className="grid grid-cols-12 gap-3 p-3 hover:bg-navy-700/50 transition-colors group"
                title={`Salvo em: ${formattedDate}`}
              >
                <div className="col-span-4 flex flex-col gap-1.5 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                    <span
                      className="text-sm text-white font-medium truncate"
                      title={arquivo.arquivoCaminho || arquivo.arquivoNome}
                    >
                      {arquivo.arquivoNome}
                    </span>
                    {arquivo.favorito && (
                      <Star className="w-3 h-3 text-yellow-400 fill-current flex-shrink-0" />
                    )}
                  </div>
                  <ArquivoMetaLinha arquivo={arquivo} />
                </div>

                <div className="col-span-3 flex flex-col gap-0.5 min-w-0">
                  <span className="text-sm text-white font-semibold truncate" title={clienteDisplay.destaque}>
                    {clienteDisplay.destaque}
                  </span>
                  {clienteDisplay.secundarios.map((linha) => (
                    <span key={linha} className="text-[11px] text-gray-400 truncate" title={linha}>
                      {linha}
                    </span>
                  ))}
                </div>

                <div className="col-span-2 flex items-center min-w-0">
                  <span className="text-[11px] text-gray-400 truncate" title={`Salvo em: ${formattedDate}`}>
                    {formattedDate}
                  </span>
                </div>

                <div className="col-span-3 flex items-center gap-1 justify-end">
                  {podeFavoritarArquivo(arquivo) && (
                    <button
                      onClick={() => onToggleFavorito(arquivo)}
                      className={`p-1 rounded transition-all ${
                        arquivo.favorito
                          ? 'text-yellow-400 hover:text-yellow-300 bg-yellow-400/20 hover:bg-yellow-400/30'
                          : 'text-gray-400 hover:text-yellow-400 hover:bg-yellow-400/10'
                      }`}
                      title={arquivo.favorito ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                      aria-label={arquivo.favorito ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                    >
                      <Star className={`w-3.5 h-3.5 ${arquivo.favorito ? 'fill-current' : ''}`} />
                    </button>
                  )}
                  <button
                    onClick={() => abrirHabilitado && onAbrirArquivo(arquivo)}
                    disabled={!abrirHabilitado}
                    className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded transition-all whitespace-nowrap ${
                      abrirHabilitado
                        ? 'text-blue-300 bg-blue-500/10 hover:bg-blue-500/20'
                        : 'text-gray-500 bg-navy-700/50 cursor-not-allowed opacity-60'
                    }`}
                    title={tooltipAbrirArquivo(arquivo)}
                    aria-label="Abrir arquivo no CorelDRAW"
                    aria-disabled={!abrirHabilitado}
                  >
                    <PackageCheck className="w-3.5 h-3.5" />
                    <span className="hidden lg:inline">Abrir</span>
                  </button>
                  <button
                    onClick={() => setArquivoDetalhes(arquivo)}
                    className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-gray-300 bg-navy-700 hover:bg-navy-600 rounded transition-all whitespace-nowrap"
                    title="Ver detalhes completos"
                    aria-label="Ver detalhes completos"
                  >
                    <Info className="w-3.5 h-3.5" />
                    <span className="hidden lg:inline">Detalhes</span>
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        </div>

        <div className="sm:hidden space-y-2">
          {arquivos.map((arquivo) => {
            const formattedDate = formatArquivoData(arquivo.dataCriacao);
            const clienteDisplay = buildClienteDisplay(arquivo.cliente);
            const abrirHabilitado = podeAbrirArquivo(arquivo);

            return (
            <div
              key={arquivo.id}
              className="bg-navy-800/50 rounded-lg p-2.5 hover:bg-navy-700/50 transition-colors border border-navy-700/50"
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-1.5 min-w-0 flex-1">
                  <FileText className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                  <span className="text-xs text-white font-medium truncate" title={arquivo.arquivoNome}>
                    {arquivo.arquivoNome}
                  </span>
                </div>
                {podeFavoritarArquivo(arquivo) && (
                  <button
                    onClick={() => onToggleFavorito(arquivo)}
                    className={`p-1 rounded transition-all flex-shrink-0 ${
                      arquivo.favorito
                        ? 'text-yellow-400 bg-yellow-400/20'
                        : 'text-gray-500 hover:text-yellow-400 hover:bg-yellow-400/10'
                    }`}
                    title={arquivo.favorito ? 'Remover dos favoritos' : 'Favoritar'}
                  >
                    <Star className={`w-3 h-3 ${arquivo.favorito ? 'fill-current' : ''}`} />
                  </button>
                )}
              </div>

              <div className="mb-2">
                <ArquivoMetaLinha arquivo={arquivo} />
              </div>

              <div className="flex flex-col gap-0.5 mb-2">
                <span className="text-xs text-white font-semibold truncate">
                  {clienteDisplay.destaque}
                </span>
                {clienteDisplay.secundarios.map((linha) => (
                  <span key={linha} className="text-[10px] text-gray-400 truncate">
                    {linha}
                  </span>
                ))}
              </div>

              <div className="mb-2 flex items-center gap-1 text-[10px] text-gray-400" title={`Salvo em: ${formattedDate}`}>
                <Clock className="w-3 h-3 text-blue-300 flex-shrink-0" />
                <span className="truncate">Salvo em {formattedDate}</span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => abrirHabilitado && onAbrirArquivo(arquivo)}
                  disabled={!abrirHabilitado}
                  className={`flex-1 inline-flex items-center justify-center gap-1.5 py-1.5 text-[11px] font-medium rounded transition-all ${
                    abrirHabilitado
                      ? 'text-blue-300 bg-blue-500/10 hover:bg-blue-500/20'
                      : 'text-gray-500 bg-navy-700/50 cursor-not-allowed opacity-60'
                  }`}
                  title={tooltipAbrirArquivo(arquivo)}
                  aria-disabled={!abrirHabilitado}
                >
                  <PackageCheck className="w-3.5 h-3.5" />
                  <span>Abrir arquivo</span>
                </button>
                <button
                  onClick={() => setArquivoDetalhes(arquivo)}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 py-1.5 text-[11px] font-medium text-gray-300 bg-navy-700 hover:bg-navy-600 rounded transition-all"
                >
                  <Info className="w-3.5 h-3.5" />
                  <span>Ver detalhes</span>
                </button>
              </div>
            </div>
            );
          })}
        </div>
      </div>

      {arquivoDetalhes && (
        <ArquivoDetalhesModal
          arquivo={arquivoDetalhes}
          onClose={() => setArquivoDetalhes(null)}
          onAbrirArquivo={onAbrirArquivo}
        />
      )}
    </>
  );
}

export const ListaArquivos = memo(ListaArquivosComponent);
